import React, { useState, useEffect } from 'react';
import { Key, Layers, Save, X, ShieldCheck, AlertCircle } from 'lucide-react';

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (keys: string[]) => void;
  savedKeys: string[];
}

export const ApiKeyModal: React.FC<ApiKeyModalProps> = ({ isOpen, onClose, onSave, savedKeys }) => {
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [singleKey, setSingleKey] = useState('');
  const [batchKeys, setBatchKeys] = useState('');

  useEffect(() => {
    if (savedKeys.length > 1) {
      setMode('batch');
      setBatchKeys(savedKeys.join('\n'));
      setSingleKey('');
    } else if (savedKeys.length === 1) {
      setMode('single');
      setSingleKey(savedKeys[0]);
      setBatchKeys('');
    }
  }, [savedKeys, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    let keysToSave: string[] = [];
    
    if (mode === 'single') {
      if (singleKey.trim()) keysToSave = [singleKey.trim()];
    } else {
      keysToSave = batchKeys
        .split('\n')
        .map(k => k.trim())
        .filter(k => k.length > 0);
    }

    onSave(keysToSave);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-lg bg-[#141517] border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/5 bg-[#1A1B1E]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <ShieldCheck size={20} className="text-blue-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white tracking-wide">API Configuration</h2>
              <p className="text-xs text-slate-400">Manage your Gemini API keys</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-white/5 rounded-md text-slate-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Tabs */}
          <div className="flex p-1 bg-[#1A1B1E] rounded-lg border border-white/5">
            <button
              onClick={() => setMode('single')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 text-xs font-medium rounded-md transition-all ${
                mode === 'single' 
                  ? 'bg-[#2C2E33] text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Key size={14} />
              Single Key
            </button>
            <button
              onClick={() => setMode('batch')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 text-xs font-medium rounded-md transition-all ${
                mode === 'batch' 
                  ? 'bg-[#2C2E33] text-white shadow-sm' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Layers size={14} />
              Batch Keys
            </button>
          </div>

          <div className="min-h-[160px]">
            {mode === 'single' ? (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Gemini API Key
                </label>
                <input
                  type="password"
                  value={singleKey}
                  onChange={(e) => setSingleKey(e.target.value)}
                  placeholder="AIza..."
                  className="w-full bg-[#0B0C0E] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all font-mono"
                  autoFocus
                />
                <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                  <AlertCircle size={12} />
                  Keys are stored locally in your browser.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider flex justify-between">
                  <span>List of API Keys</span>
                  <span className="text-blue-400">{batchKeys.split('\n').filter(k => k.trim()).length} keys</span>
                </label>
                <textarea
                  value={batchKeys}
                  onChange={(e) => setBatchKeys(e.target.value)}
                  placeholder={"AIza...\nAIza...\nAIza..."}
                  className="w-full h-32 bg-[#0B0C0E] border border-white/10 rounded-lg px-4 py-3 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all font-mono resize-none leading-relaxed"
                  spellCheck={false}
                />
                <p className="text-[11px] text-slate-500">
                  Paste one key per line. The app will rotate through them automatically to handle rate limits.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/5 bg-[#1A1B1E] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-slate-100 hover:bg-white text-black text-xs font-bold rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-white/5"
          >
            <Save size={14} />
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};